import React, {useEffect, useMemo} from 'react';
import {useStyles} from './utils/VoicemailsDialog.utils';
import {useVoicemails} from './hooks/useVoicemails';
import VoicemailsContent from './components/VoicemailsContent';
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from 'react-redux';
import {ReduxState} from '../../store/types';
import {ServiceFeatureName} from '../../store/types/ServiceFeature';
import {YesNo} from '../../store/types/CallScreening';
import InfoBanner from '../../components/InfoBanner/InfoBanner';
import {actions} from '../../store';
import Loader from '../../components/Loader/Loader';
import AudioPlayer from '../../components/AudioPlayer/AudioPlayer';

export interface VoicemailListProps {
    onFilteredCountChange?: (count: number) => void;
}

export const VoicemailList: React.FC<VoicemailListProps> = ({ onFilteredCountChange }) => {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const {accountDetails, isLoadingAccountDetails} = useSelector((state: ReduxState) => state.accountControlPanel);
    const {accountVoicemails} = useSelector((state: ReduxState) => state.voicemails);

    const unifiedMessagingEnabled = useMemo(() => {
        return accountDetails?.service_features?.find(e => e.name == ServiceFeatureName.UnifiedMessaging && e.effective_flag_value == YesNo.Yes) != undefined;
    }, [accountDetails?.service_features]);

    const voicemails = useVoicemails({
        // No i_account = account user viewing their own voicemails
        i_account: undefined,
        extensionId: accountDetails?.extension_id || '',
        extensionName: accountDetails?.extension_name || '',
    });

    const { fetchData, isLoadingVoicemails, filteredCount} = voicemails;

    useEffect(() => {
        if (onFilteredCountChange) {
            onFilteredCountChange(filteredCount);
        }
    }, [filteredCount, onFilteredCountChange]);

    const isInitialVoicemailLoad = accountVoicemails === undefined ||
        (isLoadingVoicemails && (!accountVoicemails || accountVoicemails.length === 0));

    useEffect(() => {
        dispatch(actions.getAccountPanelAccountDetails.request());
    }, [dispatch]);

    useEffect(() => {
        if(accountDetails && unifiedMessagingEnabled) {
            fetchData();
        }
    }, [fetchData, accountDetails, unifiedMessagingEnabled]);

    if(isLoadingAccountDetails || !accountDetails) {
        return <Loader dataQa="voicemail-list-loader" absolutePosition />;
    }

    if(!unifiedMessagingEnabled) {
        return <InfoBanner text={t('screens:voicemails.voicemailDisabled')} />;
    }

    if(isInitialVoicemailLoad) {
        return <Loader dataQa="voicemail-list-loader" absolutePosition />;
    }

    return (
        // @ts-ignore
        <div className={classes.mainContainer}>
            <VoicemailsContent
                voicemails={voicemails}
                showLoadingInGrid={isLoadingVoicemails || voicemails.data === undefined}
            />
            <div style={{ height: 0, overflow: 'hidden', position: 'absolute' }}>
                <AudioPlayer
                    visible={false}
                    dataQa="voicemail-hidden-player"
                    dataTestId="voicemail-hidden-player"
                />
            </div>
        </div>
    );
};

export default VoicemailList;